import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Row, Col, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import useApi from '../../hooks/useApi'
import CharityItem from './CharityItem'

const PAGE_SIZE = 20

export default function CharityPickerModal({
  show,
  onHide,
  onSelectCharity,
  currentDefaultCharityId,
  favoriteCharityName,
  mode = 'select',
  disabled,
}) {
  const { t } = useTranslation()
  const { charitiesSearch } = useApi()

  const pinnedCharity = useMemo(() => {
    if (mode !== 'change' || !currentDefaultCharityId || !favoriteCharityName) {
      return null
    }
    return {
      id: currentDefaultCharityId,
      organization_id: currentDefaultCharityId,
      organization_name: favoriteCharityName,
    }
  }, [mode, currentDefaultCharityId, favoriteCharityName])

  const modalTitle =
    mode === 'change' && favoriteCharityName
      ? t('charities.picker.change_favorite_charity', {
          charityName: favoriteCharityName,
        })
      : t('charities.picker.select_favorite_charity')
  const [charities, setCharities] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [subtext, setSubtext] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const query = useRef('')

  const fetchSearchResults = useCallback(async () => {
    if (query.current?.trim().length) {
      setIsSearching(true)
      const q = query.current.trim()
      try {
        const { data } = await charitiesSearch({
          query: q,
          page: currentPage,
          perPage: PAGE_SIZE,
        })
        if (data) {
          if (data.results && data.results.length > 0) {
            if (currentPage === 1) {
              setCharities(data.results)
            } else {
              setCharities(prev => [...prev, ...data.results])
            }
            setTotalCount(data.total_count ?? 0)
            setTotalPages(data.total_pages ?? 1)
            setShowResults(true)
            const startRange = (currentPage - 1) * PAGE_SIZE + 1
            const endRange = Math.min(currentPage * PAGE_SIZE, data.total_count)
            setSubtext(
              t('charities.search.search_results', {
                range: `${startRange}–${endRange}`,
                total: data.total_count,
              })
            )
          } else {
            if (currentPage === 1) {
              setCharities([])
              setShowResults(true)
              setSubtext(t('charities.search.no_results'))
            }
          }
        }
      } catch (error) {
        setCharities([])
        setSubtext('')
        setShowResults(false)
        // eslint-disable-next-line no-alert
        alert('Failed to search charities. Please try again.')
      } finally {
        setIsSearching(false)
      }
    }
  }, [charitiesSearch, currentPage, t])

  const handleSearch = useCallback(
    e => {
      if (e) {
        e.preventDefault()
      }
      if (!query.current?.trim().length) {
        return
      }
      setCurrentPage(1)
      setTotalCount(0)
      setTotalPages(1)
      setSubtext('')
      setCharities([])
      setShowResults(false)
      fetchSearchResults()
    },
    [fetchSearchResults],
  )

  // Debounced search that triggers after user stops typing (500ms delay)
  const debouncedSearch = useDebouncedCallback(
    (searchQuery) => {
      if (searchQuery?.trim().length >= 2) {
        query.current = searchQuery
        setCurrentPage(1)
        setTotalCount(0)
        setTotalPages(1)
        setSubtext('')
        setCharities([])
        setShowResults(false)
        // Trigger search by calling fetch logic directly
        setIsSearching(true)
        const q = searchQuery.trim()
        charitiesSearch({
          query: q,
          page: 1,
          perPage: PAGE_SIZE,
        }).then(({ data }) => {
          if (data) {
            if (data.results && data.results.length > 0) {
              setCharities(data.results)
              setTotalCount(data.total_count ?? 0)
              setTotalPages(data.total_pages ?? 1)
              setShowResults(true)
              const startRange = 1
              const endRange = Math.min(PAGE_SIZE, data.total_count)
              setSubtext(
                t('charities.search.search_results', {
                  range: `${startRange}–${endRange}`,
                  total: data.total_count,
                })
              )
            } else {
              setCharities([])
              setShowResults(true)
              setSubtext(t('charities.search.no_results'))
            }
          }
        }).catch(() => {
          setCharities([])
          setSubtext('')
          setShowResults(false)
        }).finally(() => {
          setIsSearching(false)
        })
      } else {
        setCharities([])
        setShowResults(false)
        setSubtext('')
      }
    },
    500,
    { leading: false, trailing: true }
  )

  const handleLoadMore = useCallback(() => {
    if (currentPage < totalPages && !isSearching) {
      setCurrentPage(prev => prev + 1)
    }
  }, [currentPage, totalPages, isSearching])

  const handleSelect = useCallback(
    charity => {
      onSelectCharity(charity)
      onHide()
    },
    [onSelectCharity, onHide],
  )

  const handleClose = useCallback(() => {
    setCharities([])
    setCurrentPage(1)
    setTotalCount(0)
    setTotalPages(1)
    setSubtext('')
    setShowResults(false)
    query.current = ''
    onHide()
  }, [onHide])

  useEffect(() => {
    if (query.current?.trim().length && currentPage > 1) {
      fetchSearchResults()
    }
  }, [currentPage, fetchSearchResults])

  const displayCharities = useMemo(() => {
    if (!pinnedCharity) {
      return charities
    }
    const withoutPinned = charities.filter(
      charity =>
        (charity?.id || charity?.organization_id) !== currentDefaultCharityId,
    )
    return [pinnedCharity, ...withoutPinned]
  }, [charities, pinnedCharity, currentDefaultCharityId])

  const showCharityList =
    displayCharities.length > 0 &&
    (showResults || (mode === 'change' && pinnedCharity))

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col className="col-9">
            <input
              className="form-control form-control-sm"
              id="charitySearch"
              placeholder={t('charities.search.search_placeholder')}
              onChange={e => {
                const value = e.target.value
                query.current = value
                // Trigger debounced search as user types (requires at least 2 characters)
                if (value.trim().length >= 2) {
                  debouncedSearch(value)
                } else {
                  setCharities([])
                  setShowResults(false)
                  setSubtext('')
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSearch(e)
                }
              }}
            />
          </Col>
          <Col className="col-3 d-grid">
            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={!query.current?.trim().length || disabled || isSearching}
              className="btn-sm"
            >
              {isSearching ? 'Searching...' : t('charities.search.search_button')}
            </Button>
          </Col>
        </Row>
        {showResults && subtext && (
          <Row className="mb-2">
            <Col>
              <div className="small text-muted">
                {subtext} <b>{query.current}</b>
              </div>
            </Col>
          </Row>
        )}
        {showCharityList && (
          <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
            {displayCharities.map(charity => {
              const charityId = charity?.id || charity?.organization_id
              return (
                <CharityItem
                  key={charityId}
                  charity={charity}
                  onSelect={handleSelect}
                  isSelected={currentDefaultCharityId === charityId}
                  disabled={disabled}
                />
              )
            })}
            {showResults && currentPage < totalPages && (
              <div className="text-center mt-3 mb-3">
                <Button
                  variant="outline-secondary"
                  onClick={handleLoadMore}
                  disabled={isSearching || disabled}
                  size="sm"
                >
                  {isSearching ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        )}
        {showResults && charities.length === 0 && (
          <div className="text-center text-muted py-4">
            {t('charities.search.no_results')}
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}
